import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import {
    SITE_NAME,
    buildAbsoluteUrl,
    buildPageTitle,
    resolveMetaText,
} from "../../utils/pageMeta";

export default function PageSeo({
    title,
    description,
    keywords,
    path,
    imagePath = "/og-image.png",
    imageAlt,
    type = "website",
    noIndex = false,
    structuredData,
    children,
}) {
    const location = useLocation();
    const pagePath = path ?? location.pathname ?? "/";
    const pageTitle = buildPageTitle(resolveMetaText(title));
    const pageDescription = resolveMetaText(description);
    const pageKeywords = resolveMetaText(keywords);
    const canonical = buildAbsoluteUrl(pagePath);
    const image = imagePath ? buildAbsoluteUrl(imagePath) : "";
    const imageAltText = resolveMetaText(imageAlt) || pageTitle;
    const robots = noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large";

    return (
        <Helmet>
            <html lang="en" />
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {pageKeywords ? <meta name="keywords" content={pageKeywords} /> : null}
            <meta name="author" content={SITE_NAME} />
            <meta name="robots" content={robots} />
            <meta name="googlebot" content={robots} />
            <meta name="application-name" content={SITE_NAME} />
            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang="en" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="en_US" />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:url" content={canonical} />
            {image ? <meta property="og:image" content={image} /> : null}
            {image ? <meta property="og:image:secure_url" content={image} /> : null}
            {imageAltText ? <meta property="og:image:alt" content={imageAltText} /> : null}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            {image ? <meta name="twitter:image" content={image} /> : null}
            {imageAltText ? <meta name="twitter:image:alt" content={imageAltText} /> : null}

            {structuredData ? (
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            ) : null}

            {children}
        </Helmet>
    );
}
